import React, { useState } from 'react';
import { BookOpen, Plus, Trash2, Save, X } from 'lucide-react';
import { toast } from 'react-toastify';

export default function CurriculumModal({ 
  isOpen, 
  onClose, 
  initialSyllabus = [], 
  onSave 
}) {
  const [modules, setModules] = useState(initialSyllabus);
  const [newModuleTitle, setNewModuleTitle] = useState('');

  const handleAddModule = () => {
    if (newModuleTitle.trim()) {
      setModules([...modules, newModuleTitle.trim()]);
      setNewModuleTitle('');
    } else {
      toast.warning("Please enter a module title");
    }
  };

  const handleRemoveModule = (index) => {
    if (modules.length > 1) {
      setModules(modules.filter((_, i) => i !== index));
    } else {
      toast.warning("Course must have at least one module");
    }
  };

  const handleUpdateModule = (index, value) => {
    const updatedModules = [...modules];
    updatedModules[index] = value;
    setModules(updatedModules);
  };

  const handleSaveCurriculum = () => {
    onSave(modules);
    onClose();
    toast.success("Curriculum updated successfully!");
  };

  const handleCloseModal = () => {
    setModules(initialSyllabus);
    onClose();
    setNewModuleTitle('');
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
      onClick={handleCloseModal}
    >
      <div 
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <BookOpen size={24} className="text-orange-500" />
            <h3 className="text-xl font-semibold text-gray-800">Course Curriculum</h3>
          </div>
          <button
            onClick={handleCloseModal}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              Manage your course modules. Drag to reorder or edit titles as needed.
            </p>
          </div>

          {/* Modules List */}
          <div className="space-y-3 mb-6">
            {modules.map((module, index) => (
              <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center text-sm font-semibold text-orange-600 flex-shrink-0">
                  {index + 1}
                </div>
                <input
                  type="text"
                  value={module}
                  onChange={(e) => handleUpdateModule(index, e.target.value)}
                  className="flex-1 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
                  placeholder={`Module ${index + 1} title`}
                />
                <button
                  onClick={() => handleRemoveModule(index)}
                  className="p-2 text-gray-400 hover:text-red-500 rounded-lg transition-colors flex-shrink-0"
                  disabled={modules.length <= 1}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          {/* Add New Module */}
          <div className="flex gap-3 items-center">
            <input
              type="text"
              value={newModuleTitle}
              onChange={(e) => setNewModuleTitle(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddModule()}
              placeholder="Enter new module title..."
              className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button
              onClick={handleAddModule}
              className="px-4 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              <Plus size={16} />
              Add Module
            </button>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={handleCloseModal}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveCurriculum}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
          >
            <Save size={16} />
            Save Curriculum
          </button>
        </div>
      </div>
    </div>
  );
}